import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { MapContext, MapContextType } from './map-context'
import { AttributionControl, LngLatBoundsLike, LngLatLike, Map as MaplibreMap } from 'maplibre-gl'
import { DEFAULT_MAP_ZOOM, MAP_MIN_ZOOM } from '../constants'
import { twMerge } from 'tailwind-merge'

export interface BaseMapProps {
  mapId: string
  initialCenter?: LngLatLike | null
  initialBounds?: LngLatBoundsLike | null
  initialZoom?: number
  interactive?: boolean
  containerClassName?: string
  children: ReactNode
  mapStyle?: string
}

export function BaseMap({
  mapId,
  initialCenter = null,
  initialBounds = null,
  initialZoom = DEFAULT_MAP_ZOOM,
  interactive = true,
  containerClassName,
  children,
  mapStyle = 'https://api.maptiler.com/maps/3b3332d0-1859-4926-b9fc-2200ddcbf2fd/style.json?key=8RsMVBwRMyJFBmvgmQWQ',
}: BaseMapProps) {
  const [map, setMap] = useState<MaplibreMap | null>(null)
  const [mapLoaded, setMapLoaded] = useState<boolean>(false)

  // -------------------------------------------------------------------------------------------------------
  // Create the Map.
  // -------------------------------------------------------------------------------------------------------
  useEffect(() => {
    const newMap = new MaplibreMap({
      style: mapStyle,
      container: mapId,
      dragPan: true,
      dragRotate: false,
      pitchWithRotate: false,
      scrollZoom: true,
      minZoom: MAP_MIN_ZOOM,
      doubleClickZoom: true,
      attributionControl: false,
      logoPosition: 'bottom-right',
      interactive,

      // NOTE:  If there are no initial bounds, then the map will use the default center. If no default center is
      //        specified then the library will use the center saved in the map's style:
      //        https://maplibre.org/maplibre-gl-js-docs/api/map/#map-parameters:~:text=browser%20window%20resizes.-,options.center,-LngLatLike
      bounds: initialBounds || undefined, // If specified, overrides both center & zoom.
      center: initialCenter || undefined,

      // NOTE:  The value for initialZoom should NEVER be undefined because it causes the map to crash with
      //        "Error: Failed to invert matrix" and there currently isn't a fix.
      //        References:
      //          - https://github.com/mapbox/mapbox-gl-js/pull/6924 (old fix but issue still occurs)
      //          - https://github.com/mapbox/mapbox-gl-js/issues/6782
      //          - https://github.com/mapbox/mapbox-gl-js/issues/6486
      //          - https://github.com/mapbox/mapbox-gl-js/pull/6921
      zoom: initialZoom,
    })

    setMap(newMap)

    newMap.addControl(new AttributionControl({ compact: true }))

    return () => {
      setMapLoaded(false)
      newMap.remove()
      setMap(null)
    }
  }, [mapId, initialBounds, initialCenter, mapStyle, interactive, initialZoom])

  // -------------------------------------------------------------------------------------------------------
  // error
  // -------------------------------------------------------------------------------------------------------
  const handleError = useCallback((e: { error: { message: string } }) => {
    console.warn(`<BaseMap /> error:`, e)
  }, [])

  useEffect(() => {
    map?.on('error', handleError)

    return () => {
      map?.off('error', handleError)
    }
  }, [handleError, map])

  // -------------------------------------------------------------------------------------------------------
  // load
  // -------------------------------------------------------------------------------------------------------
  const handleMapLoaded = useCallback(() => {
    if (!map) {
      return
    }

    setMapLoaded(true)
  }, [map])

  useEffect(() => {
    map?.on('load', handleMapLoaded)

    return () => {
      map?.off('load', handleMapLoaded)
    }
  }, [map, handleMapLoaded])

  const mapContext = useMemo((): MapContextType => {
    return { map, mapLoaded }
  }, [map, mapLoaded])

  return (
    <MapContext.Provider value={mapContext}>
      <div id={mapId} className={twMerge('relative h-full w-full', containerClassName)}>
        {children}
      </div>
    </MapContext.Provider>
  )
}

export default BaseMap
